<template>
  <div>
    <b-card>
      <b-overlay
        :show="loading"
        blur="10px"
        opacity="0.30"
        rounded="sm"
        variant="light"
      >
        <b-form
          @submit.prevent
          @submit="setMerchantSetting"
        >
          <b-tabs>
            <!-- Tab 1: Merchant Type -->
            <b-tab
              title="Merchant Type"
              active
            >
              <b-card title="Merchant Type (Cash/Credit)">
                <b-form-radio-group
                  v-model="merchantType"
                  :options="radioOptions"
                  class="demo-inline-spacing"
                  value-field="value"
                  text-field="text"
                  disabled-field="disabled"
                />
              </b-card>
              <p class="text-muted ml-2">
                If this mode is on, the Merchant Type (Cash/Credit) Setting will be shown in both merchant and staff portal.
              </p>
            </b-tab>

            <!-- Tab 2: Merchant Pickup Request -->
            <b-tab title="Pickup Request">
              <b-card title="Merchant Pickup Request">
                <b-form-radio-group
                  v-model="pickupEnable"
                  :options="radioOptions"
                  class="demo-inline-spacing"
                  value-field="value"
                  text-field="text"
                  disabled-field="disabled"
                />
              </b-card>
              <p class="text-muted ml-2">
                When enabled, 'Merchant Pickup' is accessible in both merchant and staff portals. Disabling this option will set 'Merchant Pickup' to 'Disabled' for all clients. Please note that if there are any pending pickup requests with 'Pickup Request' enabled, they must be cleared before disabling this setting.
              </p>
            </b-tab>

            <!-- Tab 3: Order Exchange Block -->
            <b-tab title="Order Exchange Block">
              <b-card title="Order Exchange Block">
                <b-form-radio-group
                  v-model="exchangeBlocked"
                  :options="radioOptions"
                  class="demo-inline-spacing"
                  value-field="value"
                  text-field="text"
                  disabled-field="disabled"
                />
              </b-card>
              <p class="text-muted ml-2">
                When enabled, Rider need to collect the return order from customer before exchange.
              </p>
            </b-tab>

            <!-- Tab 4: Verification Document Upload -->
            <b-tab title="Document Upload">
              <b-card title="Merchant Verification Document Upload">
                <b-form-radio-group
                  v-model="verifyDocUpload"
                  :options="radioOptions"
                  class="demo-inline-spacing mb-1"
                  value-field="value"
                  text-field="text"
                  disabled-field="disabled"
                />
              </b-card>
              <p class="text-muted ml-2">
                When this setting enabled, merchants will be REQUIRED to upload proof documents such as NIC, BR, Bank Detail Copy to the system during the registration process.
              </p>
            </b-tab>

            <!-- Tab 5: Image Scan Upload -->
            <b-tab
              v-can="'edit-ocr-upload-setting'"
              title="Image Scan Upload"
            >
              <b-card title="Image Scan Upload">
                <b-form-radio-group
                  v-model="imageScanUploadEnable"
                  :options="radioOptions"
                  class="demo-inline-spacing mb-1"
                  value-field="value"
                  text-field="text"
                  disabled-field="disabled"
                />
              </b-card>
              <p class="text-muted ml-2">
                When this setting is enabled, you can choose and grant access to merchants, allowing them to upload pictures of their handwritten orders using the sample format we provide as a guide. Disabling this option will set 'OCR Upload' to 'Disabled' for all Merchants.
              </p>
            </b-tab>

            <!-- Tab 6: Auto Merchant Inactivation -->
            <b-tab title="Auto Inactivation">
              <b-card title="Auto Merchant Inactivation">
                <b-form-radio-group
                  v-model="autoMerchantInactivationEnable"
                  :options="radioOptions"
                  class="demo-inline-spacing mb-1"
                  value-field="value"
                  text-field="text"
                  disabled-field="disabled"
                />
                <b-row
                  v-if="autoMerchantInactivationEnable"
                  class="mt-1"
                >
                  <b-col md="6">
                    <label for="auto-merchant-inactivation-time-unit">Time Unit</label>
                    <v-select
                      id="auto-merchant-inactivation-time-unit"
                      v-model="autoMerchantInactivationTimeUnit"
                      :options="timeUnitOptions"
                      :reduce="opt => opt.value"
                      placeholder="Select Time Unit"
                    />
                  </b-col>
                  <b-col md="6">
                    <label for="auto-merchant-inactivation-value">Value</label>
                    <b-form-input
                      id="auto-merchant-inactivation-value"
                      v-model="autoMerchantInactivationValue"
                      type="number"
                      min="1"
                      step="1"
                      placeholder="Enter value"
                    />
                  </b-col>
                </b-row>
              </b-card>
              <p class="text-muted ml-2">
                Enabling this setting automatically inactivates merchants with no orders during the specified timeframe. Disabling this option keeps merchants active regardless of the last order date, requiring inactivation to be made manually.
              </p>
            </b-tab>
          </b-tabs>

          <!-- Submit Button -->
          <b-row class="mt-2">
            <b-col class="d-flex justify-content-end">
              <b-button
                v-can="'edit-merchant-setting'"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                class="mt-1 custom-button-color"
              >
                Submit
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BRow,
  BCard,
  BOverlay,
  BFormRadioGroup,
  BCol,
  BForm,
  BButton,
  BFormInput,
  BTabs,
  BTab,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import Filter from '@core/mixins/filter'
import vSelect from 'vue-select'

const MerchantSettingsRepository = RepositoryFactory.get('merchantSetting')

export default {
  components: {
    BRow,
    BCard,
    BOverlay,
    BFormRadioGroup,
    BCol,
    BForm,
    BButton,
    BFormInput,
    BTabs,
    BTab,
    vSelect,
  },
  directives: {
    Ripple,
  },
  mixins: [SuccessMessage, ErrorMessage, Filter],
  data() {
    return {
      dataLoading: false,
      loading: false,
      form: {},
      radioOptions: [
        { text: 'On', value: true },
        { text: 'Off', value: false },
      ],
      timeUnitOptions: [
        { label: 'Days', value: 'days' },
        { label: 'Weeks', value: 'weeks' },
        { label: 'Months', value: 'months' },
      ],
      merchantType: false,
      pickupEnable: false,
      verifyDocUpload: false,
      exchangeBlocked: true,
      imageScanUploadEnable: false,
      autoMerchantInactivationEnable: false,
      autoMerchantInactivationTimeUnit: null,
      autoMerchantInactivationValue: null,
    }
  },
  mounted() {
    this.readMerchantSetting()
  },
  methods: {
    async readMerchantSetting() {
      this.loading = true
      try {
        const { data } = (await MerchantSettingsRepository.getMerchantSettingsResource()).data
        this.form = data
        this.merchantType = this.form.merchant_type_select
        this.pickupEnable = this.form.merchant_pickup_enable
        this.verifyDocUpload = this.form.verify_document_upload_enable
        this.exchangeBlocked = this.form.block_exchange
        this.imageScanUploadEnable = this.form.image_scan_upload_enable
        this.autoMerchantInactivationEnable = this.form.merchant_inactivation_enable
        this.autoMerchantInactivationTimeUnit = this.form.merchant_inactivation_unit
        this.autoMerchantInactivationValue = this.form.merchant_inactivation_value
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    validateMerchantInactivation() {
      if (!this.autoMerchantInactivationEnable) {
        return true
      }
      if (!this.autoMerchantInactivationTimeUnit) {
        this.showErrorMessage('Please select a time unit for merchant inactivation')
        return false
      }
      if (
        this.autoMerchantInactivationValue == null
        || this.autoMerchantInactivationValue === ''
      ) {
        this.showErrorMessage('Please enter a value for merchant inactivation')
        return false
      }
      return true
    },
    async setMerchantSetting() {
      if (!this.validateMerchantInactivation()) {
        return
      }
      this.loading = true
      try {
        const payload = {
          merchant_type_select: this.merchantType,
          merchant_pickup_enable: this.pickupEnable,
          verify_document_upload_enable: this.verifyDocUpload,
          block_exchange: this.exchangeBlocked,
          image_scan_upload_enable: this.imageScanUploadEnable,
          merchant_inactivation_enable: this.autoMerchantInactivationEnable,
          merchant_inactivation_unit: this.autoMerchantInactivationTimeUnit,
          merchant_inactivation_value: this.autoMerchantInactivationValue != null
            && this.autoMerchantInactivationValue !== ''
            ? parseInt(this.autoMerchantInactivationValue, 10)
            : null,
        }
        const res = await MerchantSettingsRepository.updateMerchantSettingsResource(payload)
        if (res.status === 200) {
          this.showSuccessMessage('Merchant Settings Updated Successfully')
        } else {
          this.showErrorMessage('Cannot Update Merchant Settings')
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.readMerchantSetting()
      this.loading = false
    },
  },
}
</script>

<style scoped>
.custom-button-color {
  background-color: #4186f5 !important;
  border-color: #4186f5 !important;
}
.add-margin-bottom {
  margin-bottom: 20px;
}
@media (max-width: 767px) {
  .b-table thead th {
    position: sticky;
    top: 0;
  }
  .add-margin-bottom {
    width: 100%;
  }
}
</style>
